import { createSelector } from '@reduxjs/toolkit';

import { type RootState } from '@verantos-dev/core-annotation-tool';

import { SUPERVISOR_REVIEW_TAB_OPTIONS } from '../constants';

// Stable empty array reference to avoid unnecessary re-renders
const EMPTY_ARRAY: never[] = [];

export const selectFilterBy = (state: RootState) => state.supervisor?.filterBy ?? 'all';
export const selectNoteSortBy = (state: RootState) => state.supervisor?.noteSortBy ?? 'ascending';
export const selectGroupBy = (state: RootState) => state.supervisor?.groupBy ?? 'none';
export const selectSelectedNoteId = (state: RootState) => state.supervisor?.selectedNoteId ?? null;
export const selectAnnotatorOneAllConceptsExpanded = (state: RootState) =>
  state.supervisor?.annotatorOneAllConceptsExpanded ?? undefined;
export const selectAnnotatorTwoAllConceptsExpanded = (state: RootState) =>
  state.supervisor?.annotatorTwoAllConceptsExpanded ?? undefined;
export const selectConflictsAllConceptsExpanded = (state: RootState) =>
  state.supervisor?.conflictsAllConceptsExpanded ?? undefined;
export const selectResolvedAllConceptsExpanded = (state: RootState) =>
  state.supervisor?.resolvedAllConceptsExpanded ?? undefined;
export const selectAnnotatorOneExpandedConceptIds = (state: RootState) =>
  state.supervisor?.annotatorOneExpandedConceptIds ?? [];
export const selectAnnotatorTwoExpandedConceptIds = (state: RootState) =>
  state.supervisor?.annotatorTwoExpandedConceptIds ?? [];
export const selectAddEvidenceModeForConceptId = (state: RootState) =>
  state.supervisor?.addEvidenceModeForConceptId ?? null;
export const selectHighlightedTextSpans = (state: RootState) =>
  state.supervisor?.highlightedTextSpans ?? [];
export const selectConflictIdsInReplaceMode = (
  state: RootState
): { conflictId: string; annotationIdsToReject: string[] }[] =>
  state.supervisor?.conflictIdsInReplaceMode ?? [];
export const selectAnnotationIdsToRejectByConflictId = (conflictId: string) =>
  createSelector(
    [selectConflictIdsInReplaceMode],
    (conflictIdsInReplaceMode) =>
      conflictIdsInReplaceMode.find((entry) => entry.conflictId === conflictId)
        ?.annotationIdsToReject ?? EMPTY_ARRAY
  );
export const selectResolvedAnnotationIdsInReplaceMode = (
  state: RootState
): { conceptId: string; annotationIdToDelete: string }[] =>
  state.supervisor?.resolvedAnnotationIdsInReplaceMode ?? [];
export const selectResolvedReplaceModeByConceptId = (conceptId: string) =>
  createSelector(
    [selectResolvedAnnotationIdsInReplaceMode],
    (entries) => entries.find((entry) => entry.conceptId === conceptId) ?? null
  );
// // Memoized selector for pending spans by supervisor ID
const selectPendingSpansState = (state: RootState) => state.supervisor?.pendingSpans ?? {};

export const selectPendingSpansForAnnotation = (supervisorId: string) =>
  createSelector(
    [selectPendingSpansState],
    (pendingSpans) => pendingSpans[supervisorId] ?? EMPTY_ARRAY
  );

export const selectViewOptionExpanded = (state: RootState) =>
  state.supervisor?.viewOptionExpanded ?? false;
export const selectShowAnnotations = (state: RootState) =>
  state.supervisor?.showAnnotations ?? false;
export const selectReviewTabSelected = (state: RootState) =>
  state.supervisor?.reviewTabSelected ?? SUPERVISOR_REVIEW_TAB_OPTIONS[0];
export const selectConflictExpandedConceptIds = (state: RootState) =>
  state.supervisor?.conflictExpandedConceptIds ?? [];
export const selectResolvedExpandedConceptIds = (state: RootState) =>
  state.supervisor?.resolvedExpandedConceptIds ?? [];
export const selectDeleteSpanError = (supervisorId: string) => (state: RootState) =>
  state.supervisor?.deleteSpanError[supervisorId] ?? null;
export const selectCreateAnnotationError = (conceptId: string) => (state: RootState) =>
  state.supervisor?.createAnnotationError[conceptId] ?? null;
export const selectEditAnnotationError = (supervisorId: string) => (state: RootState) =>
  state.supervisor?.editAnnotationError[supervisorId] ?? null;
export const selectConflictAcceptError = (conflictId: string) => (state: RootState) =>
  state.supervisor?.conflictAcceptError[conflictId] ?? null;
export const selectConflictRejectError = (conflictId: string) => (state: RootState) =>
  state.supervisor?.conflictRejectError[conflictId] ?? null;
export const selectDeleteResolvedAnnotationError = (annotationId: string) => (state: RootState) =>
  state.supervisor?.deleteResolvedAnnotationError[annotationId] ?? null;
export const selectDeleteResolvedAnnotationErrors = (state: RootState) =>
  state.supervisor?.deleteResolvedAnnotationError ?? {};
export const selectMarkSupervisionCompleteError = (state: RootState) =>
  state.supervisor?.markSupervisionCompleteError ?? null;
export const selectAnnotationIdsInEditMode = (state: RootState) =>
  state.supervisor?.annotationIdsInEditMode ?? [];
export const selectEvidenceIdsToDelete = (state: RootState) =>
  state.supervisor?.evidenceIdsToDelete ?? {};
export const selectFinalizedConceptIdsInCreateMode = (state: RootState) =>
  state.supervisor?.finalizedConceptIdsInCreateMode ?? [];
export const selectDirtyNewAnnotationConceptIds = (state: RootState) =>
  state.supervisor?.dirtyNewAnnotationConceptIds ?? [];
